import { h, vaciar, m2, num, textoAgua } from '../dom';
import { anadir } from '../dom';
import type { Contexto } from '../contexto';
import type { Ficha } from '../../db/consultas';
import type { Inmueble } from '../../db/tipos';
import { urlFotoFachada, urlSedeCatastro, nombrePlanta } from '../../catastro/ovc';
import { nombreUso, nombreEstado } from '../../catastro/gml';
import { prepararModelo, modeloListo, asegurarEntornoParcela, cubreMargen, margenModelo, areaCompartida, aplicarAlturas } from '../../lidar/preparar';
import { husoParaLonLat } from '../../geo/proyecciones';
import { claveModelo, guardarModelo, type EstadoArea } from '../../lidar/api';

/** Datos minimos cuando la parcela no esta en la base local (clic fuera de la zona cargada). */
export interface FichaMinima {
  rc: string;
  direccion: string | null;
  /** Huso en el que pedir la parcela al Catastro (el del punto pulsado); si falta, el del centro del mapa. */
  epsg?: number;
}

/** «Ver 3D» desde un enlace compartido. */
export interface OpcionesVer3d {
  /** Area del servidor del enlace: si sigue alli, se abre sin calcular nada. */
  areaId?: string | null;
  /** Desfase del enlace: si hay que calcular, con al menos ese margen. */
  margenM?: number | null;
  /** Catastro de la parcela y su entorno que ya esta bajando quien abre el enlace. */
  entorno?: Promise<Ficha | null>;
}

/** Panel de ficha de parcela (lateral derecho). */
export class FichaPanel {
  readonly el = h('aside', { class: 'panel-ficha oculto' });
  private rc: string | null = null;
  private epsgMinima: number | null = null;
  private inmuebles: Inmueble[] = [];
  private cargandoInmuebles = false;
  onCerrar: (() => void) | null = null;
  onExportarDxf: ((rc: string) => void) | null = null;
  onInformePdf: ((rc: string) => void) | null = null;
  onCompartir: ((rc: string) => void) | null = null;
  /** Abre la escena del modelo ya preparado (la aplicacion lleva la direccion de la pagina). */
  onVer3d: ((rc: string, area: EstadoArea) => Promise<void>) | null = null;

  constructor(private readonly ctx: Contexto) {}

  cerrar(): void {
    this.el.classList.add('oculto');
    this.rc = null;
  }

  mostrar(ficha: Ficha | null, minima?: FichaMinima): void {
    const rc = ficha?.parcela.rc ?? minima?.rc ?? null;
    if (!rc) { this.cerrar(); return; }
    if (rc !== this.rc) {
      this.epsgMinima = null;
      this.inmuebles = ficha?.inmuebles ?? [];
      if (ficha) void this.recuperarModelo(rc);
    }
    this.rc = rc;
    if (minima?.epsg) this.epsgMinima = minima.epsg;
    this.el.classList.remove('oculto');
    vaciar(this.el);
    const esCandidato = this.ctx.almacen.estado.candidatos.some((c) => c.rc === rc);
    const p = ficha?.parcela, e = ficha?.edificio ?? null;

    anadir(this.el,
      h('div', { class: 'ficha-cabecera' },
        h('div', null, h('div', { class: 'rc grande' }, rc), h('div', { class: 'silencio' }, minima?.direccion ?? this.inmuebles[0]?.direccion ?? (p ? `Parcela nº ${p.etiqueta ?? '—'}` : ''))),
        h('button', { class: 'cerrar', title: 'Cerrar', onClick: () => { this.cerrar(); this.onCerrar?.(); } }, '×')),
      h('img', { class: 'foto', src: urlFotoFachada(rc), alt: 'Foto de fachada del Catastro', loading: 'lazy', onError: (ev: Event) => { (ev.target as HTMLElement).style.display = 'none'; } }),
      h('div', { class: 'fila-botones' },
        h('button', { class: `boton ${esCandidato ? 'secundario' : 'primario'}`, onClick: () => void this.ctx.alternarCandidato(rc) }, esCandidato ? '★ Quitar de candidatos' : '☆ Marcar como candidato'),
        p ? h('button', { class: 'boton secundario', onClick: () => this.ctx.mapa.encajarBbox([p.minx, p.miny, p.maxx, p.maxy], p.epsg, 19) }, 'Centrar') : null,
        h('button', { class: 'boton secundario', title: 'Enlace a esta parcela', onClick: () => this.onCompartir?.(rc) }, 'Compartir')),
      p ? this.seccionParcela(ficha!) : h('p', { class: 'silencio' }, 'Esta parcela no está en la zona cargada: solo se muestran los datos del Catastro en línea. «Ver 3D» descarga lo necesario de la parcela y su entorno.'),
      e ? this.seccionEdificio(ficha!) : null,
      ficha && ficha.partes.length ? this.seccionPartes(ficha) : null,
      this.seccionInmuebles(rc),
      this.seccionLidar(rc),
      h('div', { class: 'fila-botones' },
        h('a', { class: 'boton secundario', href: urlSedeCatastro(rc), target: '_blank', rel: 'noopener' }, 'Sede del Catastro'),
        p ? h('button', { class: 'boton secundario', onClick: () => this.onExportarDxf?.(rc) }, 'DXF') : null,
        p ? h('button', { class: 'boton secundario', onClick: () => this.onInformePdf?.(rc) }, 'PDF') : null),
      h('p', { class: 'silencio pie' }, 'Datos: © Dirección General del Catastro (no protegidos). La foto de fachada la sirve el Catastro con origen no oficial.'),
    );
  }

  private fila(nombre: string, valor: string | number | null | undefined): HTMLElement {
    return h('div', { class: 'ficha-fila' }, h('span', { class: 'clave' }, nombre), h('span', { class: 'valor' }, valor === null || valor === undefined ? '—' : String(valor)));
  }

  private seccionParcela(f: Ficha): HTMLElement {
    const p = f.parcela;
    return h('div', { class: 'ficha-seccion' }, h('h3', null, 'Parcela'),
      this.fila('Superficie', m2(p.areaM2)),
      this.fila('Agua más cercana (IGN)', textoAgua(p.distAguaM, p.aguaNombre)),
      this.fila('Edificios', p.nEdificios),
      this.fila('Cuerpos (partes)', p.nPartes || '—'),
      this.fila('Sistema', `EPSG:${p.epsg}`));
  }

  private seccionEdificio(f: Ficha): HTMLElement {
    const e = f.edificio!;
    return h('div', { class: 'ficha-seccion' }, h('h3', null, 'Edificio'),
      this.fila('Uso', nombreUso(e.uso)),
      this.fila('Estado', nombreEstado(e.estado)),
      this.fila('Año de construcción', e.anio),
      this.fila('Plantas sobre rasante', e.plantas),
      this.fila('Superficie construida', m2(e.supConstruidaM2)),
      this.fila('Huella (calculada)', m2(e.huellaM2)),
      this.fila('Viviendas', e.viviendas),
      this.fila('Unidades', e.unidades),
      e.aleroM !== null ? this.fila('Altura de alero (LiDAR)', `${num(e.aleroM, 1)} m`) : null,
      e.cumbreraM !== null ? this.fila('Altura de cumbrera (LiDAR)', `${num(e.cumbreraM, 1)} m`) : null,
      e.desviacionM !== null ? this.fila('Desviación huella-LiDAR', `${num(e.desviacionM, 2)} m`) : null);
  }

  private seccionPartes(f: Ficha): HTMLElement {
    return h('div', { class: 'ficha-seccion' }, h('h3', null, `Cuerpos del edificio (${f.partes.length})`),
      h('table', { class: 'tabla' },
        h('thead', null, h('tr', null, h('th', null, 'Huella'), h('th', null, 'Sobre'), h('th', null, 'Bajo'))),
        h('tbody', null, ...f.partes.map((x) => h('tr', null, h('td', null, m2(x.huellaM2)), h('td', null, x.plantasSobre ?? '—'), h('td', null, x.plantasBajo ?? '—'))))));
  }

  private seccionInmuebles(rc: string): HTMLElement {
    const lista = this.inmuebles;
    return h('div', { class: 'ficha-seccion' },
      h('h3', null, `Inmuebles${lista.length ? ` (${lista.length})` : ''}`),
      lista.length === 0
        ? h('button', { class: 'boton secundario', disabled: this.cargandoInmuebles, onClick: () => void this.consultarInmuebles(rc) }, this.cargandoInmuebles ? 'Consultando…' : 'Consultar inmuebles al Catastro')
        : h('table', { class: 'tabla' },
          h('thead', null, h('tr', null, h('th', null, 'Planta'), h('th', null, 'Puerta'), h('th', null, 'm²'), h('th', null, 'Uso'), h('th', null, 'Año'))),
          h('tbody', null, ...lista.map((i) => h('tr', null, h('td', null, nombrePlanta(i.planta)), h('td', null, i.puerta ?? '—'), h('td', null, i.superficieM2 ?? '—'), h('td', null, i.uso ?? '—'), h('td', null, i.anio ?? '—'))))),
      lista.length ? h('button', { class: 'enlace', onClick: () => void this.consultarInmuebles(rc, true) }, 'actualizar') : null);
  }

  // ---------- Modelo LiDAR de la parcela (backend) ----------

  private lidarEstado: EstadoArea | null = null;
  private lidarOcupado = false;
  private lidarControl: AbortController | null = null;

  private lidarMensaje: string | null = null;

  private seccionLidar(rc: string): HTMLElement {
    const e = this.lidarEstado && (this.lidarEstado.rc === rc) ? this.lidarEstado : null;
    const listo = e?.estado === 'listo';
    const conModelo = listo && !!e?.productos['modelo.json'];
    const pct = e && e.hojasTotal ? Math.round((e.hojasHechas / e.hojasTotal) * 100) : 0;
    return h('div', { class: 'ficha-seccion' },
      h('h3', null, 'Modelo LiDAR 3D'),
      h('p', { class: 'silencio' }, `Descarga lo necesario (Catastro de la parcela y su entorno, LiDAR y MDT de 50 cm del IGN) y calcula cubiertas, muros con la huella del Catastro corregida, árboles, arbustos, hierba, el terreno y sus curvas, recortados a la parcela y hasta ${margenModelo()} m alrededor.`),
      this.lidarOcupado
        ? h('div', { class: 'tarjeta' },
          h('div', { class: 'barra' }, h('div', { class: 'barra-relleno', style: `width:${pct}%` })),
          h('div', null, this.lidarMensaje ?? e?.mensaje ?? e?.estado ?? 'Preparando…', e?.hojasTotal && !this.lidarMensaje ? ` · ${e.hojasHechas}/${e.hojasTotal} hojas` : ''),
          h('button', { class: 'boton secundario', onClick: () => this.lidarControl?.abort() }, 'Dejar de esperar'))
        : h('div', { class: 'fila-botones' },
          h('button', { class: 'boton primario', onClick: () => void this.verEn3d(rc, false) }, 'Ver 3D'),
          conModelo ? h('button', { class: 'boton secundario', title: 'Vuelve a descargar el Catastro del entorno y recalcula el modelo', onClick: () => void this.verEn3d(rc, true) }, 'Recalcular') : null),
      listo && e ? h('div', { class: 'silencio' },
        `${e.hojasHechas} hoja(s) · ${e.alturas.filter((a) => a.cumbreraM !== null).length} edificio(s) medido(s)`,
        e.productos['modelo.glb'] ? h('span', null, ' · ', h('a', { href: e.productos['modelo.glb'], class: 'enlace' }, 'glTF'), ' ', h('a', { href: e.productos['planta.json'], class: 'enlace' }, 'planta')) : null) : null,
      e?.estado === 'error' ? h('div', { class: 'error' }, e.error ?? 'Error') : null);
  }

  /** Recupera en silencio el modelo ya preparado de esta parcela (para ofrecer "Ver 3D" sin recalcular). */
  private async recuperarModelo(rc: string): Promise<void> {
    if (this.lidarOcupado || (this.lidarEstado && this.lidarEstado.rc === rc)) return;
    const e = await modeloListo(claveModelo({ rc }));
    if (e && this.rc === rc && !this.lidarOcupado) { this.lidarEstado = e; this.refrescar(); }
  }

  /**
   * "Ver 3D": se encarga de todo. Si la parcela no esta en la base local (o falta su entorno), baja
   * del Catastro la parcela, los edificios y las partes de alrededor; despues pide el modelo al
   * servidor (o reutiliza el ya calculado) y abre la escena recortada a la parcela. Desde un enlace
   * compartido prueba antes el area del enlace, que se abre sin calcular.
   */
  async verEn3d(rc: string, forzar: boolean, op: OpcionesVer3d = {}): Promise<void> {
    if (this.lidarOcupado) return;
    this.lidarOcupado = true;
    this.lidarControl = new AbortController();
    const senal = this.lidarControl.signal;
    this.lidarMensaje = 'Preparando…';
    this.refrescar();
    try {
      // Se abre el ya calculado solo si llega al margen actual (los de 50 m, de antes, se recalculan).
      const anterior = this.lidarEstado?.rc === rc && this.lidarEstado.estado === 'listo' && this.lidarEstado.productos['modelo.json'] ? this.lidarEstado : null;
      let e = anterior && cubreMargen(anterior, margenModelo()) ? anterior : null;
      if (op.areaId && !forzar) {
        this.lidarMensaje = 'Abriendo el modelo del enlace…';
        this.refrescar();
        const compartida = await areaCompartida(op.areaId, rc);
        if (senal.aborted) throw new Error('Cancelado');
        if (compartida) {
          e = compartida;
          this.lidarEstado = e;
          guardarModelo(claveModelo({ rc }), e.id);
          // Las alturas medidas van a la base local cuando llegue el Catastro del entorno.
          void op.entorno?.then((f) => (f ? aplicarAlturas(this.ctx, compartida) : undefined)).catch(() => undefined);
        } else {
          this.ctx.almacen.avisar('El modelo del enlace ya no está en el servidor: se calcula de nuevo.');
        }
        this.lidarMensaje = null;
      }
      if (!e || forzar) {
        try {
          const f = (await op.entorno?.catch(() => null))
            ?? await asegurarEntornoParcela(this.ctx, rc, this.epsgPreferido(), (m) => { this.lidarMensaje = m; this.refrescar(); });
          if (senal.aborted) throw new Error('Cancelado');
          void this.ctx.refrescarZona();
          this.lidarMensaje = null;
          e = await prepararModelo(this.ctx, { tipo: 'parcela', rc, geom: f.parcela.geom, epsg: f.parcela.epsg }, {
            senal, forzar,
            margenM: op.margenM ? Math.max(margenModelo(), op.margenM) : undefined,
            onEstado: (x) => { this.lidarEstado = x; this.refrescar(); },
          });
          this.lidarEstado = e;
          await this.ctx.refrescarCandidatos();
        } catch (err) {
          // Si no sale el nuevo (p. ej. a 150 m), se abre el que habia en vez de reintentar a cada clic.
          const msg = err instanceof Error ? err.message : String(err);
          if (!anterior || msg === 'Cancelado' || senal.aborted) throw err;
          this.lidarEstado = anterior;
          this.ctx.almacen.avisar(`Modelo LiDAR: no se pudo calcular de nuevo (${msg}); se abre el que ya había.`, 'error');
          e = anterior;
        }
      }
      this.lidarOcupado = false;
      this.lidarMensaje = null;
      this.refrescar();
      await this.onVer3d?.(rc, e);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg !== 'Cancelado') this.ctx.almacen.avisar(`Modelo LiDAR: ${msg}`, 'error');
    } finally {
      this.lidarOcupado = false;
      this.lidarMensaje = null;
      this.lidarControl = null;
      this.refrescar();
    }
  }

  /** Huso para pedir la parcela al Catastro: el del punto pulsado o, si no, el del centro del mapa. */
  private epsgPreferido(): number {
    if (this.epsgMinima) return this.epsgMinima;
    const c = this.ctx.mapa.map.getCenter();
    return husoParaLonLat(c.lng, c.lat);
  }

  private async consultarInmuebles(rc: string, forzar = false): Promise<void> {
    this.cargandoInmuebles = true;
    this.refrescar();
    try {
      this.inmuebles = await this.ctx.db.inmuebles(rc, forzar);
      if (this.inmuebles.length === 0) this.ctx.almacen.avisar('El Catastro no devolvió inmuebles para esta referencia.');
    } catch (err) {
      this.ctx.almacen.avisar(`No se pudieron consultar los inmuebles: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      this.cargandoInmuebles = false;
      this.refrescar();
      await this.ctx.refrescarCandidatos();
    }
  }

  private refrescar(): void {
    if (!this.rc) return;
    void this.ctx.db.ficha(this.rc).then((f) => this.mostrar(f, f ? undefined : { rc: this.rc!, direccion: this.inmuebles[0]?.direccion ?? null }));
  }

  get rcActual(): string | null {
    return this.rc;
  }
}
